import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  useMediaQuery,
  useTheme,
  Skeleton,
  TablePagination,
  Button,
  Stack,
} from '@mui/material';
import config from '../../Config';
import AddTTLockForm from './AddTtLock';

export default function LockManagement() {
  const [locks, setLocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [addingLock, setAddingLock] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [countdown, setCountdown] = useState('');
  const [tokenLoading, setTokenLoading] = useState(false);

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchLocks = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${config.apiBaseUrl}/ttlock/getAll`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setLocks(data.data);
      } else {
        console.error('Failed to fetch locks:', data.message);
      }
    } catch (err) {
      console.error('Error fetching locks:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTTLockTimer = async () => {
    try {
      setTokenLoading(true);

      const token = localStorage.getItem('token');

      const res = await fetch(`${config.apiBaseUrl}/ttlock/token/countdown`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setTokenInfo(data);

        setCountdown(
          `${data.countdown.days}d ${data.countdown.hours}h ${data.countdown.minutes}m ${data.countdown.seconds}s`
        );
      }
    } catch (err) {
      console.error('Error fetching TTLock token:', err);
    } finally {
      setTokenLoading(false);
    }
  };

  const refreshTTLockToken = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${config.apiBaseUrl}/ttlock/ttlock-access-token`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        alert('TTLock Token Updated Successfully');

        getTTLockTimer();
      } else {
        alert(data.message || 'Failed to update token');
      }
    } catch (error) {
      console.error('TTLock Update Error:', error);
      alert('Error updating TTLock token');
    }
  };

  useEffect(() => {
    fetchLocks();
    getTTLockTimer();
  }, []);

  useEffect(() => {
    if (!tokenInfo?.expiresAt) return;

    const interval = setInterval(() => {
      const expiry = new Date(tokenInfo.expiresAt);
      const now = new Date();

      const remaining = expiry - now;

      if (remaining <= 0) {
        setCountdown('Expired');
        clearInterval(interval);
        return;
      }

      const totalSeconds = Math.floor(remaining / 1000);

      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      setCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    }, 1000);

    return () => clearInterval(interval);
  }, [tokenInfo]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const renderSkeletonRows = (count = 5) =>
    Array.from({ length: count }).map((_, i) => (
      <TableRow key={i}>
        <TableCell>
          <Skeleton width={100} />
        </TableCell>
        <TableCell>
          <Skeleton width={120} />
        </TableCell>
        <TableCell>
          <Skeleton width={120} />
        </TableCell>
        <TableCell>
          <Skeleton width={100} />
        </TableCell>
        <TableCell>
          <Skeleton width={60} height={28} variant="rounded" />
        </TableCell>
      </TableRow>
    ));

  const paginatedLocks = locks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (addingLock) {
    return (
      <Box sx={{ px: 3, py: 2 }}>
        <AddTTLockForm
          onCancel={() => setAddingLock(false)}
          onSuccess={() => {
            setAddingLock(false);
            fetchLocks();
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant={isSmDown ? 'h5' : 'h4'} fontWeight="bold">
          Smart Locks
        </Typography>
        {/* <Button variant="contained" size="large" onClick={() => setAddingLock(true)}>
          Update TT Lock
        </Button> */}
      </Stack>

      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold">
            TTLock Access Token
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Expiry Date:
          </Typography>

          <Typography variant="body1">
            {tokenInfo?.expiresAt ? new Date(tokenInfo.expiresAt).toLocaleString() : '-'}
          </Typography>

          <Typography
            variant="h6"
            color={
              countdown === 'Expired'
                ? 'error.main'
                : tokenInfo?.shouldRefresh
                  ? 'warning.main'
                  : 'success.main'
            }
            sx={{ mt: 1 }}
          >
            {countdown}
          </Typography>
        </Box>

        <Stack direction="row" spacing={2}>
          <Chip
            label={
              countdown === 'Expired'
                ? 'Expired'
                : tokenInfo?.shouldRefresh
                  ? 'Refresh Soon'
                  : 'Active'
            }
            color={
              countdown === 'Expired' ? 'error' : tokenInfo?.shouldRefresh ? 'warning' : 'success'
            }
          />

          <Button variant="contained" onClick={getTTLockTimer} disabled={tokenLoading}>
            {tokenLoading ? 'Refreshing...' : 'Refresh Status'}
          </Button>

          <Button variant="contained" color="success" onClick={refreshTTLockToken}>
            Update TTLock Token
          </Button>
        </Stack>
      </Paper>
      <Paper elevation={3} sx={{ p: isSmDown ? 2 : 3, borderRadius: 3 }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell>Lock ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Alias</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Has Gateway</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                renderSkeletonRows(6)
              ) : paginatedLocks.length > 0 ? (
                paginatedLocks.map((lock) => (
                  <TableRow key={lock._id}>
                    <TableCell>{lock.lockId}</TableCell>
                    <TableCell>{lock.lockName}</TableCell>
                    <TableCell>{lock.lockAlias}</TableCell>
                    <TableCell>{lock.groupName || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={lock.hasGateway ? 'Yes' : 'No'}
                        color={lock.hasGateway ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    No locks found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        {!loading && locks.length > rowsPerPage && (
          <TablePagination
            component="div"
            count={locks.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[]}
          />
        )}
      </Paper>
    </Box>
  );
}
